import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';

// ============================================================================
// INTERFACES
// ============================================================================

export interface StatsFilters {
  // Time filters
  dateRange: { start: Date; end: Date };
  comparisonEnabled: boolean;
  comparisonType: 'previous' | 'year_ago' | null;
  granularity: 'day' | 'week' | 'month' | 'quarter' | 'year';

  // Quick filters
  activePreset:
    | 'today'
    | 'week'
    | 'month'
    | 'quarter'
    | 'year'
    | 'custom'
    | null;

  // Entity filters
  siteIds: number[];
  treatmentIds: number[];
  treatmentCategoryIds: number[];
  productIds: number[];
  productCategoryIds: number[];
  patientIds: number[];
  paymentMethods: string[];
}

export interface ComparisonPeriod {
  start: Date;
  end: Date;
  label: string;
}

// ============================================================================
// SERVICE
// ============================================================================

@Injectable({
  providedIn: 'root',
})
export class StatsFilterService {
  // Default filters
  private defaultFilters: StatsFilters = {
    dateRange: {
      start: moment().startOf('month').toDate(),
      end: moment().endOf('month').toDate(),
    },
    comparisonEnabled: true,
    comparisonType: 'previous',
    granularity: 'day',
    activePreset: 'month',
    siteIds: [],
    treatmentIds: [],
    treatmentCategoryIds: [],
    productIds: [],
    productCategoryIds: [],
    patientIds: [],
    paymentMethods: [],
  };

  private filtersSubject = new BehaviorSubject<StatsFilters>({
    ...this.defaultFilters,
  });
  public filters$ = this.filtersSubject.asObservable();

  constructor(private translate: TranslateService) {
    this.setMomentLocale(this.translate.currentLang);
    this.translate.onLangChange.subscribe(({ lang }) =>
      this.setMomentLocale(lang),
    );
  }

  // ============================================================================
  // GETTERS
  // ============================================================================

  get currentFilters(): StatsFilters {
    return this.filtersSubject.getValue();
  }

  get dateRange(): { start: Date; end: Date } {
    return this.currentFilters.dateRange;
  }

  get comparisonPeriod(): ComparisonPeriod | null {
    if (!this.currentFilters.comparisonEnabled) return null;

    const { start, end } = this.currentFilters.dateRange;
    const duration = moment(end).diff(moment(start), 'days');

    if (this.currentFilters.comparisonType === 'previous') {
      return {
        start: moment(start)
          .subtract(duration + 1, 'days')
          .toDate(),
        end: moment(start).subtract(1, 'day').toDate(),
        label: this.translate.instant('STATISTICS.FILTERS.COMPARISON.PREVIOUS'),
      };
    } else if (this.currentFilters.comparisonType === 'year_ago') {
      return {
        start: moment(start).subtract(1, 'year').toDate(),
        end: moment(end).subtract(1, 'year').toDate(),
        label: this.translate.instant('STATISTICS.FILTERS.COMPARISON.YEAR_AGO'),
      };
    }
    return null;
  }

  /**
   * Get current filters with comparison period calculated
   * Used by data service for API calls
   */
  getCurrentFilters(): StatsFilters & {
    comparisonPeriod: ComparisonPeriod | null;
  } {
    return {
      ...this.currentFilters,
      comparisonPeriod: this.comparisonPeriod,
    };
  }

  getActiveFilterCount(): number {
    const filters = this.currentFilters;
    let count = 0;
    if (filters.siteIds.length) count++;
    if (filters.treatmentIds.length) count++;
    if (filters.treatmentCategoryIds.length) count++;
    if (filters.productIds.length) count++;
    if (filters.patientIds.length) count++;
    if (filters.paymentMethods.length) count++;
    return count;
  }

  // ============================================================================
  // PRESETS
  // ============================================================================

  setToday(): void {
    this.updateFilters({
      dateRange: {
        start: moment().startOf('day').toDate(),
        end: moment().endOf('day').toDate(),
      },
      activePreset: 'today',
      granularity: 'day',
    });
  }

  setThisWeek(): void {
    this.updateFilters({
      dateRange: {
        start: moment().startOf('week').toDate(),
        end: moment().endOf('week').toDate(),
      },
      activePreset: 'week',
      granularity: 'day',
    });
  }

  setThisMonth(): void {
    this.updateFilters({
      dateRange: {
        start: moment().startOf('month').toDate(),
        end: moment().endOf('month').toDate(),
      },
      activePreset: 'month',
      granularity: 'day',
    });
  }

  setThisQuarter(): void {
    this.updateFilters({
      dateRange: {
        start: moment().startOf('quarter').toDate(),
        end: moment().endOf('quarter').toDate(),
      },
      activePreset: 'quarter',
      granularity: 'week',
    });
  }

  setThisYear(): void {
    this.updateFilters({
      dateRange: {
        start: moment().startOf('year').toDate(),
        end: moment().endOf('year').toDate(),
      },
      activePreset: 'year',
      granularity: 'month',
    });
  }

  setCustomRange(start: Date, end: Date): void {
    const duration = moment(end).diff(moment(start), 'days');
    let granularity: StatsFilters['granularity'] = 'day';
    if (duration > 90) granularity = 'week';
    if (duration > 365) granularity = 'month';

    this.updateFilters({
      dateRange: { start, end },
      activePreset: 'custom',
      granularity,
    });
  }

  shiftActivePeriod(direction: 'previous' | 'next'): void {
    const { activePreset, dateRange } = this.currentFilters;

    if (!activePreset) return;

    const shift = direction === 'next' ? 1 : -1;

    if (activePreset === 'custom') {
      const durationDays =
        moment(dateRange.end).diff(moment(dateRange.start), 'days') + 1;
      const start = moment(dateRange.start)
        .add(shift * durationDays, 'days')
        .toDate();
      const end = moment(dateRange.end)
        .add(shift * durationDays, 'days')
        .toDate();

      this.setCustomRange(start, end);
      return;
    }

    switch (activePreset) {
      case 'today': {
        this.updateFilters({
          dateRange: {
            start: moment(dateRange.start)
              .add(shift, 'day')
              .startOf('day')
              .toDate(),
            end: moment(dateRange.end).add(shift, 'day').endOf('day').toDate(),
          },
          activePreset: 'today',
          granularity: 'day',
        });
        break;
      }
      case 'week': {
        const start = moment(dateRange.start)
          .add(shift, 'week')
          .startOf('week')
          .toDate();
        const end = moment(dateRange.start)
          .add(shift, 'week')
          .endOf('week')
          .toDate();
        this.updateFilters({
          dateRange: { start, end },
          activePreset: 'week',
          granularity: 'day',
        });
        break;
      }
      case 'month': {
        const start = moment(dateRange.start)
          .add(shift, 'month')
          .startOf('month')
          .toDate();
        const end = moment(dateRange.start)
          .add(shift, 'month')
          .endOf('month')
          .toDate();
        this.updateFilters({
          dateRange: { start, end },
          activePreset: 'month',
          granularity: 'day',
        });
        break;
      }
      case 'quarter': {
        const start = moment(dateRange.start)
          .add(shift, 'quarter')
          .startOf('quarter')
          .toDate();
        const end = moment(dateRange.start)
          .add(shift, 'quarter')
          .endOf('quarter')
          .toDate();
        this.updateFilters({
          dateRange: { start, end },
          activePreset: 'quarter',
          granularity: 'week',
        });
        break;
      }
      case 'year': {
        const start = moment(dateRange.start)
          .add(shift, 'year')
          .startOf('year')
          .toDate();
        const end = moment(dateRange.start)
          .add(shift, 'year')
          .endOf('year')
          .toDate();
        this.updateFilters({
          dateRange: { start, end },
          activePreset: 'year',
          granularity: 'month',
        });
        break;
      }
    }
  }

  // ============================================================================
  // COMPARISON
  // ============================================================================

  enableComparison(type: 'previous' | 'year_ago' = 'previous'): void {
    this.updateFilters({
      comparisonEnabled: true,
      comparisonType: type,
    });
  }

  disableComparison(): void {
    this.updateFilters({
      comparisonEnabled: false,
      comparisonType: null,
    });
  }

  toggleComparison(): void {
    if (this.currentFilters.comparisonEnabled) {
      this.disableComparison();
    } else {
      this.enableComparison();
    }
  }

  // ============================================================================
  // ENTITY FILTERS
  // ============================================================================

  setSiteFilter(siteIds: number[]): void {
    this.updateFilters({ siteIds });
  }

  setTreatmentFilter(treatmentIds: number[]): void {
    this.updateFilters({ treatmentIds });
  }

  setTreatmentCategoryFilter(treatmentCategoryIds: number[]): void {
    this.updateFilters({ treatmentCategoryIds });
  }

  setProductFilter(productIds: number[]): void {
    this.updateFilters({ productIds });
  }

  setPatientFilter(patientIds: number[]): void {
    this.updateFilters({ patientIds });
  }

  setPaymentMethodFilter(paymentMethods: string[]): void {
    this.updateFilters({ paymentMethods });
  }

  // ============================================================================
  // RESET
  // ============================================================================

  resetFilters(): void {
    this.filtersSubject.next({ ...this.defaultFilters });
  }

  resetEntityFilters(): void {
    this.updateFilters({
      siteIds: [],
      treatmentIds: [],
      treatmentCategoryIds: [],
      productIds: [],
      productCategoryIds: [],
      patientIds: [],
      paymentMethods: [],
    });
  }

  // ============================================================================
  // UTILITY
  // ============================================================================

  private updateFilters(partial: Partial<StatsFilters>): void {
    this.filtersSubject.next({
      ...this.currentFilters,
      ...partial,
    });
  }

  // Format dates for API requests
  getApiDateParams(): { dateFrom: string; dateTo: string } {
    const { start, end } = this.currentFilters.dateRange;
    return {
      dateFrom: moment(start).format('YYYY-MM-DD'),
      dateTo: moment(end).format('YYYY-MM-DD'),
    };
  }

  getComparisonApiDateParams(): { dateFrom: string; dateTo: string } | null {
    const comparison = this.comparisonPeriod;
    if (!comparison) return null;
    return {
      dateFrom: moment(comparison.start).format('YYYY-MM-DD'),
      dateTo: moment(comparison.end).format('YYYY-MM-DD'),
    };
  }

  // Get formatted date range label
  getDateRangeLabel(): string {
    this.setMomentLocale(this.translate.currentLang);

    const { start, end } = this.currentFilters.dateRange;
    const isSameMonth = moment(start).isSame(end, 'month');
    const isSameYear = moment(start).isSame(end, 'year');

    if (moment(start).isSame(end, 'day')) {
      return moment(start).format('D MMMM YYYY');
    }
    if (isSameMonth) {
      return `${moment(start).format('D')} - ${moment(end).format('D MMMM YYYY')}`;
    }
    if (isSameYear) {
      return `${moment(start).format('D MMM')} - ${moment(end).format('D MMM YYYY')}`;
    }
    return `${moment(start).format('D MMM YYYY')} - ${moment(end).format('D MMM YYYY')}`;
  }

  private setMomentLocale(lang?: string): void {
    const normalized = (lang || 'es').toLowerCase();
    if (normalized.startsWith('en')) {
      moment.locale('en');
      return;
    }
    if (normalized.startsWith('ca')) {
      moment.locale('ca');
      return;
    }
    moment.locale('es');
  }
}
